import React, { FC } from 'react';
import { Box, Divider, Stack, Link, LinkProps } from '@mui/material';
import { colors } from 'styles/colors';
import { ReactComponent as BlockedIcon } from 'assets/blocked.svg';
import { ReactComponent as CheckBorderlessIcon } from 'assets/checkBorderless.svg';
import { ReactComponent as CrossIcon } from 'assets/cross.svg';
import { useTranslation } from 'react-i18next';
import CandidateInterviewFeedbackAverageRating from './candidateInterviewFeedbackAverageRating';
import CandidateInterviewFeedbackAverageRatingSkeleton from './candidateInterviewFeedbackAverageRatingSkeleton';
import CandidateInterviewFeedbackCountsSkeleton from './candidateInterviewFeedbackCountsSkeleton';
import { InterviewFeedbackQuestionType } from 'models/enum';
import { ICandidateInterviewFeedbackOverallRating } from 'modules/hiring/models/candidateFeedbackOverallRating.model';

interface ICandidateInterviewFeedbackRatingInfoProps {
  displayAverageRating?: boolean;
  aggregateData: ICandidateInterviewFeedbackOverallRating | null;
  loading: boolean;
}

const CandidateInterviewFeedbackRatingInfo = ({
  displayAverageRating,
  aggregateData,
  loading,
}: ICandidateInterviewFeedbackRatingInfoProps) => {
  const { t } = useTranslation();

  const yesNoUnsureOption =
    aggregateData?.agreegateResponse?.responseItemList?.find(
      (d, i) => d.questionType === InterviewFeedbackQuestionType.YES_NO_NA
    );
  const recommend: number = Number(yesNoUnsureOption?.selectedOptions.yes),
    notRecommend: number = Number(yesNoUnsureOption?.selectedOptions.no),
    unsure: number = Number(yesNoUnsureOption?.selectedOptions.notSure);
  return (
    <Stack
      id='candidate-interview-feedback-rating-info'
      data-testid='candidate-interview-feedback-rating-info'
      sx={{
        p: '1.25rem',
        gap: '1.5rem',
        flexDirection: 'row',
        flexWrap: 'wrap',
      }}
    >
      {displayAverageRating && (
        <Box
          sx={{
            width: '19.25rem',
            height: '10rem',
          }}
        >
          {loading ? (
            <CandidateInterviewFeedbackAverageRatingSkeleton />
          ) : (
            <CandidateInterviewFeedbackAverageRating
              feedbackAverageList={aggregateData}
            />
          )}
        </Box>
      )}
      <Divider
        orientation='vertical'
        flexItem
        sx={{
          borderColor: colors.background.midGray,
        }}
      />
      {loading ? (
        <CandidateInterviewFeedbackCountsSkeleton />
      ) : (
        <Stack
          id='interview-feedbacks-counts'
          data-testid='interview-feedbacks-counts'
          spacing='0.625rem'
          justifyContent='center'
        >
          <CustomLink
            id='interview-feedbacks-counts-recommend'
            data-testid='interview-feedbacks-counts-recommend'
            disabled={!recommend}
          >
            <CheckBorderlessIcon height='1.5rem' />
            {t('someRecommendsHiring', {
              arg1: recommend,
            })}
          </CustomLink>
          <CustomLink
            id='interview-feedbacks-counts-not-recommend'
            data-testid='interview-feedbacks-counts-not-recommend'
            disabled={!notRecommend}
          >
            <CrossIcon height='1.5rem' />
            {t('someDoesNotRecommendHiring', { arg1: notRecommend })}
          </CustomLink>
          <CustomLink
            id='interview-feedbacks-counts-unsure'
            data-testid='interview-feedbacks-counts-unsure'
            disabled={!unsure}
          >
            <BlockedIcon height='1.5rem' /> {t('someUnsure', { arg1: unsure })}
          </CustomLink>
        </Stack>
      )}
    </Stack>
  );
};

interface ICustomLinkProps extends LinkProps {
  disabled?: boolean;
}

const CustomLink: FC<ICustomLinkProps> = ({ disabled, ...props }) => (
  <Link
    {...props}
    className={disabled ? 'kfic2-bodyTextSmall' : 'kfic2-bodyTextSmallLink'}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.625rem',
      color: disabled ? `${colors.tertiary.darkGray}!important` : undefined,
      textDecoration: disabled ? 'none' : undefined,
      ...props.sx,
    }}
  />
);

export default CandidateInterviewFeedbackRatingInfo;
